'use client';

import { Card, CardContent, Typography } from '@packages/shared';
import type { MessageDescriptor } from 'react-intl';
import { useIntl } from 'react-intl';
import { startTransition, useState, type ReactNode } from 'react';
import { InstallmentCalculatorDialog } from '@packages/installment-calculator/src/installmentCalculator/InstallmentCalculatorDialog';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping/';
import { PaymentTypesFragmentFragmentDoc } from '@packages/gql/generated/shopping/PaymentTypesFragmentFragmentDoc';
import { useTheme } from '@mui/material';
import { LegalHintsImage } from '@packages/installment-calculator/src/atoms/LegalHintsImage';
import { paymentTypesMessages } from './messages';

/* GraphQL */ `
  fragment PaymentTypesFragment on Product {
    price {
      value
    }
    paymentTypes {
      keys
    }
  }
`;

function getPaymentTypeText(type: string) {
  switch (type) {
    case 'ideal': {
      return paymentTypesMessages.ideal;
    }
    case 'postpay': {
      return paymentTypesMessages.postpay;
    }
    case 'installments': {
      return paymentTypesMessages.installments;
    }
    default: {
      return null;
    }
  }
}

export type PaymentTypesProps = {
  data: FragmentType<typeof PaymentTypesFragmentFragmentDoc>;
};

export const PaymentTypes = ({ data }: PaymentTypesProps) => {
  const { price, paymentTypes } = getFragmentData(PaymentTypesFragmentFragmentDoc, data);
  const { formatMessage } = useIntl();
  const { spacing } = useTheme();
  const [dialogOpen, setDialogOpen] = useState(false);

  const texts: MessageDescriptor[] = [];

  paymentTypes?.keys.forEach((key) => {
    const text = getPaymentTypeText(key);
    if (text !== null) {
      texts.push(text);
    }
  });

  if (texts.length === 0) {
    return null;
  }

  return (
    <>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h5" mb={1.5}>
            {formatMessage(paymentTypesMessages.headline)}
          </Typography>
          <ul
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: spacing(1.5),
              listStyle: 'none',
              padding: 0,
              margin: 0,
            }}
          >
            {texts.map((text) => (
              <Typography
                key={text.id}
                variant="body2"
                component="li"
                style={{
                  position: 'relative',
                  paddingLeft: '0.688rem',
                }}
              >
                <span
                  style={{
                    position: 'absolute',
                    left: '0',
                  }}
                >
                  •
                </span>
                {formatMessage(text)}
                {text.id === 'paymentTypes.installments' && (
                  <div style={{ marginTop: spacing(0.5) }}>
                    {formatMessage(paymentTypesMessages.dialog, {
                      link: (msg: ReactNode) => (
                        <button
                          style={{
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            background: 'none',
                            border: 'none',
                          }}
                          onClick={() => startTransition(() => setDialogOpen(true))}
                          aria-haspopup="dialog"
                          type="button"
                        >
                          {msg}
                        </button>
                      ),
                    })}
                  </div>
                )}
              </Typography>
            ))}
          </ul>
        </CardContent>
        <LegalHintsImage />
      </Card>
      {dialogOpen && (
        <InstallmentCalculatorDialog
          basePrice={price.value}
          open={dialogOpen}
          handleClose={() => startTransition(() => setDialogOpen(false))}
          displayTrackingProps={{ category: 'Ratenrechner', label: 'Kredit-Promo' }}
        />
      )}
    </>
  );
};
